import { getItemCoverBlob } from "../services/collectorApi";

export const MEDIA_PX = 128;
export const EXCERPT_EXTRA_PX = 20;
export const EXCERPT_PX = MEDIA_PX + EXCERPT_EXTRA_PX;
export const MINIMAL_QR_PX = 180;
export const DOWNLOAD_DPI = 300;
export const LUXURY_WIDTH_IN = 4;
export const LUXURY_HEIGHT_IN = 6;
export const LUXURY_SIDE_PAD_IN = 0.5;
export const LUXURY_PHOTO_H_IN = 2.7;
export const LUXURY_QR_IN = 1;
/** Kept for older print CSS that still referenced a square card. */
export const LUXURY_INCH = LUXURY_WIDTH_IN;
export const MAX_LUXURY_DETAILS = 4;
export const EXCERPT_WORD_MAX = 30;
export const EXCERPT_LINE_MAX = 4;

const LUXURY_SAFE_FIELDS = [
  "itemCategory",
  "craftType",
  "color",
  "fabric",
  "clusterType",
  "occasion",
  "makerName",
] as const;

const LUXURY_FIELD_LABEL: Record<(typeof LUXURY_SAFE_FIELDS)[number], string> = {
  itemCategory: "Category",
  craftType: "Craft type",
  color: "Color",
  fabric: "Fabric",
  clusterType: "Region / cluster",
  occasion: "Occasion",
  makerName: "Company / brand / artisan",
};

export type LuxuryCardDetail = { label: string; value: string };

export type LuxuryCardFieldSource = {
  publicDisplayFields?: string[] | null;
  itemCategory?: string;
  craftType?: string;
  color?: string;
  fabric?: string;
  clusterType?: string;
  occasion?: string;
  acquiredPlace?: string;
  makerName?: string;
};

function prettyCardValue(value: string): string {
  return value.replace(/_/g, " ").trim();
}

export function clipExcerptWords(text: string, max = EXCERPT_WORD_MAX): string {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < max) {
    return text;
  }
  const clipped = words.slice(0, max).join(" ");
  if (words.length > max) {
    return clipped;
  }
  return /\s$/.test(text) ? clipped : text;
}

export function excerptWordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

/** Same allow-list as the public QR page: never price, geo, docs, or purchase place. */
export function luxuryCardDetails(
  source: LuxuryCardFieldSource | undefined,
  max = MAX_LUXURY_DETAILS
): LuxuryCardDetail[] {
  if (!source) return [];
  const allow = (source.publicDisplayFields || []).filter(Boolean);
  const details: LuxuryCardDetail[] = [];
  for (const key of LUXURY_SAFE_FIELDS) {
    if (!allow.includes(key)) continue;
    const raw = String(source[key as keyof LuxuryCardFieldSource] || "").trim();
    if (!raw) continue;
    details.push({
      label: LUXURY_FIELD_LABEL[key],
      value: prettyCardValue(raw),
    });
    if (details.length >= max) break;
  }
  return details;
}

export function fileSlug(title?: string): string {
  return (title || "piece").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "piece";
}

export function triggerDownload(dataUrl: string, filename: string) {
  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

export function svgToImage(svg: SVGElement): Promise<HTMLImageElement> {
  const serialized = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([serialized], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not render QR"));
    };
    img.src = url;
  });
}

function loadImage(src: string, crossOrigin: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = src;
  });
}

async function imageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return loadImage(URL.createObjectURL(blob), false);
}

async function loadCover(src: string | undefined, itemId?: string): Promise<HTMLImageElement | null> {
  if (itemId) {
    try {
      const res = await getItemCoverBlob(itemId);
      const blob = res.data as Blob;
      const type = blob?.type || "";
      if (blob && blob.size > 0 && !type.includes("json") && !type.includes("text")) {
        return await imageFromBlob(blob);
      }
    } catch {
      // S3 CORS often blocks canvas; fall through to a direct load.
    }
  }
  if (!src) {
    return null;
  }
  try {
    return await loadImage(src, true);
  } catch {
    return null;
  }
}

function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";

  const pushCurrent = () => {
    if (current) {
      lines.push(current);
      current = "";
    }
  };

  const fitWord = (word: string): string[] => {
    if (ctx.measureText(word).width <= maxWidth) {
      return [word];
    }
    const parts: string[] = [];
    let chunk = "";
    for (const ch of word) {
      const next = chunk + ch;
      if (ctx.measureText(next).width <= maxWidth) {
        chunk = next;
      } else {
        if (chunk) parts.push(chunk);
        chunk = ch;
      }
    }
    if (chunk) parts.push(chunk);
    return parts;
  };

  for (const word of words) {
    for (const part of fitWord(word)) {
      const next = current ? `${current} ${part}` : part;
      if (ctx.measureText(next).width <= maxWidth) {
        current = next;
      } else {
        pushCurrent();
        current = part;
        if (lines.length >= maxLines) {
          break;
        }
      }
    }
    if (lines.length >= maxLines) {
      break;
    }
  }
  if (lines.length < maxLines) {
    pushCurrent();
  }
  if (lines.length > maxLines) {
    lines.length = maxLines;
  }
  const joined = lines.join(" ");
  if (lines.length === maxLines && joined !== words.join(" ")) {
    let last = lines[maxLines - 1].replace(/…$/, "");
    while (last && ctx.measureText(`${last}…`).width > maxWidth) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = last ? `${last}…` : "…";
  }
  return lines;
}

function fillCenteredLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  centerX: number,
  startY: number,
  lineHeight: number
) {
  lines.forEach((line, index) => {
    ctx.fillText(line, centerX, startY + index * lineHeight);
  });
}

export async function drawMinimalCard(opts: {
  qrImage: HTMLImageElement;
  title: string;
}): Promise<string> {
  const scale = 3;
  const qr = MINIMAL_QR_PX * scale;
  const textW = (MINIMAL_QR_PX + EXCERPT_EXTRA_PX) * scale;
  const pad = 24 * scale;
  const titleSize = 16 * scale;
  const brandSize = 12 * scale;
  const gap = 12 * scale;
  const width = Math.max(qr, textW) + pad * 2;
  const height = pad + qr + gap + titleSize * 2.4 + gap + brandSize + pad;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create image");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  const cx = width / 2;
  ctx.drawImage(opts.qrImage, (width - qr) / 2, pad, qr, qr);

  ctx.fillStyle = "#2b2b2b";
  ctx.font = `600 ${titleSize}px "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  const titleLines = wrapLines(ctx, opts.title, textW, 2);
  fillCenteredLines(ctx, titleLines, cx, pad + qr + gap, titleSize * 1.25);

  ctx.fillStyle = "#6f6f6f";
  ctx.font = `400 ${brandSize}px "Helvetica Neue", Arial, sans-serif`;
  ctx.fillText(
    "HastKalaPramanak · My Collection",
    cx,
    pad + qr + gap + titleLines.length * titleSize * 1.25 + gap * 0.5
  );

  return canvas.toDataURL("image/png");
}

export async function drawLuxuryCard(opts: {
  qrImage: HTMLImageElement;
  title: string;
  excerpt: string;
  coverUrl?: string;
  itemId?: string;
  details?: LuxuryCardDetail[];
}): Promise<string> {
  const dpi = DOWNLOAD_DPI;
  const width = Math.round(LUXURY_WIDTH_IN * dpi);
  const height = Math.round(LUXURY_HEIGHT_IN * dpi);
  const side = Math.round(LUXURY_SIDE_PAD_IN * dpi);
  const top = Math.round(0.2 * dpi);
  const photoW = width - side * 2;
  const photoH = Math.round(LUXURY_PHOTO_H_IN * dpi);
  const qr = Math.round(LUXURY_QR_IN * dpi);
  const textW = photoW;
  const titleBox = Math.round(0.3 * dpi);
  const detailBox = Math.round(0.64 * dpi);
  const excerptBox = Math.round(0.64 * dpi);
  const details = (opts.details || []).slice(0, MAX_LUXURY_DETAILS);
  const excerpt = clipExcerptWords(opts.excerpt || "");
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create image");

  const paint = (cover: HTMLImageElement | null) => {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "#eadfce";
    ctx.lineWidth = 3;
    ctx.strokeRect(1.5, 1.5, width - 3, height - 3);

    const cx = width / 2;
    let y = top;
    const photoX = side;
    ctx.save();
    roundedRect(ctx, photoX, y, photoW, photoH, 16);
    ctx.clip();
    ctx.fillStyle = "#f5eee0";
    ctx.fillRect(photoX, y, photoW, photoH);
    if (cover) {
      const scale = Math.max(photoW / Math.max(cover.width, 1), photoH / Math.max(cover.height, 1));
      const dw = cover.width * scale;
      const dh = cover.height * scale;
      ctx.drawImage(cover, photoX + (photoW - dw) / 2, y + (photoH - dh) / 2, dw, dh);
    }
    ctx.restore();
    y += photoH + Math.round(0.1 * dpi);

    ctx.fillStyle = "#362706";
    ctx.font = `600 ${Math.round(0.13 * dpi)}px "Helvetica Neue", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const titleLines = wrapLines(ctx, opts.title, textW, 2);
    const titleLh = Math.round(0.15 * dpi);
    fillCenteredLines(ctx, titleLines, cx, y, titleLh);
    y += titleBox;

    ctx.fillStyle = "#535B62";
    ctx.font = `400 ${Math.round(0.12 * dpi)}px "Helvetica Neue", Arial, sans-serif`;
    const detailLh = Math.round(0.16 * dpi);
    details.forEach((detail, index) => {
      if (index >= MAX_LUXURY_DETAILS) return;
      const line = `${detail.label}: ${detail.value}`;
      fillCenteredLines(ctx, wrapLines(ctx, line, textW, 1), cx, y + index * detailLh, detailLh);
    });
    y += detailBox;

    ctx.fillStyle = "#2F363F";
    ctx.font = `400 ${Math.round(0.115 * dpi)}px "Helvetica Neue", Arial, sans-serif`;
    if (excerpt) {
      const excerptLh = Math.round(0.16 * dpi);
      fillCenteredLines(ctx, wrapLines(ctx, excerpt, textW, EXCERPT_LINE_MAX), cx, y, excerptLh);
    }
    y += excerptBox + Math.round(0.08 * dpi);

    ctx.drawImage(opts.qrImage, (width - qr) / 2, y, qr, qr);

    ctx.fillStyle = "#7e462a";
    ctx.font = `600 ${Math.round(0.11 * dpi)}px "Helvetica Neue", Arial, sans-serif`;
    ctx.textBaseline = "middle";
    ctx.fillText("HastKalaPramanak", cx, height - Math.round(0.16 * dpi));
  };

  const cover = await loadCover(opts.coverUrl, opts.itemId);
  paint(cover);
  return canvas.toDataURL("image/png");
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export async function downloadQrFromSvg(
  svg: SVGElement | null,
  opts: {
    title: string;
    format: "minimal" | "luxury";
    excerpt?: string;
    coverUrl?: string;
    itemId?: string;
    details?: LuxuryCardDetail[];
  }
): Promise<void> {
  if (!svg) {
    throw new Error("QR is not ready to download.");
  }
  const qrImage = await svgToImage(svg);
  const dataUrl =
    opts.format === "luxury"
      ? await drawLuxuryCard({
          qrImage,
          title: opts.title || "My Collection piece",
          excerpt: (opts.excerpt || "").trim(),
          coverUrl: opts.coverUrl,
          itemId: opts.itemId,
          details: opts.details,
        })
      : await drawMinimalCard({
          qrImage,
          title: opts.title || "My Collection piece",
        });
  const suffix = opts.format === "luxury" ? "card" : "qr";
  triggerDownload(dataUrl, `hkp-my-collection-${fileSlug(opts.title)}-${suffix}.png`);
}
